import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/** Key used to remember the last day the tooltip was shown to this visitor. */
const STORAGE_KEY = 'bl101-rewards-tooltip-shown';

/** How long to wait after page load before revealing the tooltip. */
const SHOW_DELAY = 1500;

/** How long the tooltip stays on screen before hiding itself. */
const AUTO_HIDE_DELAY = 60000;

/** Matches the CSS transition duration, so we only set `hidden` once it has faded out. */
const HIDE_TRANSITION = 300;

/** Keeps the tooltip this far away from the edge of the viewport. */
const VIEWPORT_MARGIN = 12;

/**
 * Returns today's date as a `YYYY-M-D` string in the visitor's local timezone.
 *
 * @returns {string} The local date key.
 */
function today() {
  const now = new Date();

  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

/**
 * A custom element that prompts logged out visitors to sign in, once per day.
 *
 * Wraps the account popover/drawer and reveals a tooltip beneath the account button shortly after
 * page load. The tooltip is dismissed by the close button, by interacting with the page elsewhere,
 * by pressing Escape, or automatically after a few seconds.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [tooltip] – The tooltip element.
 * @property {HTMLElement} [openButton] – The tooltip body, which opens the account popover/drawer.
 * @property {HTMLElement} [closeButton] – The dismiss button.
 *
 * @extends Component<Refs>
 *
 * @example
 * ```html
 * <account-rewards-tooltip>
 *   <!-- account popover + drawer -->
 *   <div ref="tooltip" hidden>...</div>
 * </account-rewards-tooltip>
 * ```
 *
 * @property {string} [data-force-show] - When present, ignores the once-per-day check (theme editor).
 */
export class AccountRewardsTooltip extends Component {
  #showTimer = /** @type {number | null} */ (null);
  #hideTimer = /** @type {number | null} */ (null);
  #cleanupTimer = /** @type {number | null} */ (null);
  #visible = false;

  connectedCallback() {
    super.connectedCallback();

    const { tooltip, openButton, closeButton } = this.refs;
    if (!tooltip || !this.#shouldShow()) return;

    openButton?.addEventListener('click', this.#onOpenClick);
    closeButton?.addEventListener('click', this.#hide);
    tooltip.addEventListener('pointerenter', this.#pauseAutoHide);
    tooltip.addEventListener('pointerleave', this.#scheduleAutoHide);
    this.addEventListener('click', this.#onHostClick);

    this.#showTimer = setTimeout(this.#show, SHOW_DELAY);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimers();
    this.#removeGlobalListeners();
  }

  /**
   * Whether the tooltip has already been shown to this visitor today.
   *
   * Fails open — if storage is unavailable (private browsing, blocked cookies) we still show it.
   *
   * @returns {boolean} True when the tooltip should be revealed.
   */
  #shouldShow() {
    if (this.hasAttribute('data-force-show')) return true;

    try {
      return localStorage.getItem(STORAGE_KEY) !== today();
    } catch {
      return true;
    }
  }

  #markAsShown() {
    try {
      localStorage.setItem(STORAGE_KEY, today());
    } catch {
      // Storage is unavailable — the tooltip simply isn't throttled for this visitor.
    }
  }

  #show = () => {
    const { tooltip } = this.refs;
    if (!tooltip || this.#visible) return;

    this.#markAsShown();
    this.#visible = true;

    if (this.#cleanupTimer) clearTimeout(this.#cleanupTimer);
    tooltip.hidden = false;

    // Reading layout flushes the hidden state, so the transition to visible actually animates.
    this.#reposition();
    tooltip.dataset.visible = '';

    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    document.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('resize', this.#onResize);
    this.#scheduleAutoHide();
  };

  #hide = () => {
    const { tooltip } = this.refs;
    if (!tooltip || !this.#visible) return;

    this.#visible = false;
    delete tooltip.dataset.visible;
    this.#clearTimers();
    this.#removeGlobalListeners();

    this.#cleanupTimer = setTimeout(() => {
      tooltip.hidden = true;
    }, HIDE_TRANSITION);
  };

  /**
   * Shifts the tooltip horizontally when centering it under the account button would push it past
   * the edge of the viewport. The caret is offset by the same amount so it keeps pointing at the button.
   */
  #reposition = () => {
    const { tooltip } = this.refs;
    if (!tooltip) return;

    tooltip.style.setProperty('--account-tooltip-shift', '0px');

    const rect = tooltip.getBoundingClientRect();

    // Mobile browsers widen the layout viewport — and with it `window.innerWidth` — to fit any
    // horizontal overflow, including the overflow this tooltip is causing. Measuring against it
    // therefore under-corrects and leaves the tooltip off screen. `clientWidth` is the true
    // viewport width regardless of overflow.
    const viewportWidth = document.documentElement.clientWidth;
    let shift = Math.max(0, rect.right - (viewportWidth - VIEWPORT_MARGIN));

    // Don't let the correction push the other edge off screen.
    shift -= Math.max(0, VIEWPORT_MARGIN - (rect.left - shift));

    tooltip.style.setProperty('--account-tooltip-shift', `${Math.round(shift)}px`);
  };

  #scheduleAutoHide = () => {
    if (this.#hideTimer) clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(this.#hide, AUTO_HIDE_DELAY);
  };

  #pauseAutoHide = () => {
    if (this.#hideTimer) clearTimeout(this.#hideTimer);
    this.#hideTimer = null;
  };

  #onOpenClick = () => {
    this.#hide();

    const buttons = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.account-button')]);
    const visibleButton = buttons.find((button) =>
      button.checkVisibility ? button.checkVisibility() : button.offsetParent !== null
    );

    visibleButton?.click();
  };

  /**
   * Dismisses the tooltip once the visitor opens the account popover/drawer themselves.
   *
   * @param {Event} event - The click event.
   */
  #onHostClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (target?.closest('.account-button')) this.#hide();
  };

  /**
   * @param {Event} event - The pointerdown event.
   */
  #onDocumentPointerDown = (event) => {
    const target = /** @type {Node | null} */ (event.target);
    if (target && !this.contains(target)) this.#hide();
  };

  /**
   * @param {KeyboardEvent} event - The keydown event.
   */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') this.#hide();
  };

  #onResize = debounce(() => {
    if (this.#visible) this.#reposition();
  }, 100);

  #clearTimers() {
    for (const timer of [this.#showTimer, this.#hideTimer, this.#cleanupTimer]) {
      if (timer) clearTimeout(timer);
    }

    this.#showTimer = null;
    this.#hideTimer = null;
  }

  #removeGlobalListeners() {
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    document.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('resize', this.#onResize);
  }
}

if (!customElements.get('account-rewards-tooltip')) {
  customElements.define('account-rewards-tooltip', AccountRewardsTooltip);
}
